import React, { createContext, useContext } from 'react';
import { Button, Tabs, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

const { Title } = Typography;

export const DEFAULT_SKILL_STUDIO_FOOTER_HEIGHT = 112;

interface SkillStudioLayoutContextValue {
    footerHeight: number;
}

const SkillStudioLayoutContext = createContext<SkillStudioLayoutContextValue | null>(null);

/** Returns layout context when rendered inside SkillStudio; null when used standalone. */
export const useSkillStudioLayout = () => useContext(SkillStudioLayoutContext);

export interface SkillStudioSectionOption {
    key: string;
    label: string;
    icon?: React.ReactNode;
    content: React.ReactNode;
}

export interface SkillStudioProps {
    sections: SkillStudioSectionOption[];
    onBack: () => void;
    title?: string;
    defaultActiveKey?: string;
    activeKey?: string;
    onSectionChange?: (key: string) => void;
    /** Height reserved for the global app footer. Defaults to 112px. */
    footerHeight?: number;
    containerStyle?: React.CSSProperties;
}

const SkillStudio: React.FC<SkillStudioProps> = ({
    sections,
    onBack,
    title = 'Evaluation',
    defaultActiveKey,
    activeKey,
    onSectionChange,
    footerHeight = DEFAULT_SKILL_STUDIO_FOOTER_HEIGHT,
    containerStyle,
}) => {
    const tabItems = sections.map((section) => ({
        key: section.key,
        label: (
            <span>
                {section.icon} {section.label}
            </span>
        ),
        children: (
            <div style={{ height: '100%', minHeight: 0, boxSizing: 'border-box' }}>{section.content}</div>
        ),
    }));

    return (
        <SkillStudioLayoutContext.Provider value={{ footerHeight }}>
            <div
                style={{
                    padding: 24,
                    background: '#fff',
                    boxSizing: 'border-box',
                    height:
                        footerHeight != null && footerHeight > 0
                            ? `calc(100vh - ${footerHeight}px)`
                            : '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    ...containerStyle,
                }}
            >
                <style>{`
                    .skill-studio-tabs { flex: 1; min-height: 0; display: flex; flex-direction: column; }
                    .skill-studio-tabs .ant-tabs-content-holder { flex: 1; min-height: 0; overflow: hidden; }
                    .skill-studio-tabs .ant-tabs-content { height: 100%; }
                    .skill-studio-tabs .ant-tabs-tabpane { height: 100%; }
                `}</style>
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} />
                    <Title level={4} style={{ margin: 0 }}>
                        {title}
                    </Title>
                </div>
                <Tabs
                    className="skill-studio-tabs"
                    items={tabItems}
                    defaultActiveKey={defaultActiveKey}
                    activeKey={activeKey}
                    onChange={onSectionChange}
                />
            </div>
        </SkillStudioLayoutContext.Provider>
    );
};

export default SkillStudio;
